<script lang="ts">
  import { disciplineLabel, formatEffortDuration, isDeprecatedAthleteName, ordinalSuffix, valuePrecision, zoneColorAtDeg, zoneColorForIndex, zoneConicGradient, type RadarEntry } from './rankingsTypes';
  import { round } from '@/Util/utils';

  export let entries: RadarEntry[] = [];

  const SIZE = 480;
  const CENTER = SIZE / 2;
  const RADIUS = 168;
  const RINGS = [25, 50, 75, 90, 100];
  const LABELED_RINGS = [50];

  // Duration-zone gradient (see rankingsTypes.ts's ZONE_STOPS for the color plan).
  // Used for the data-area fill and the outer border ring; the background disk
  // itself stays plain white.
  const ZONE_GRADIENT = zoneConicGradient();
  const ZONE_OPACITY = 0.2;
  const FILL_OPACITY = 0.4;
  // A crisp, full-opacity 2px ring in the same gradient traces the circle's edge.
  const BORDER_WIDTH = 5;
  const BORDER_MASK = `radial-gradient(circle closest-side, transparent calc(100% - ${BORDER_WIDTH}px), black calc(100% - ${BORDER_WIDTH}px))`;
  // The gradient disk's diameter matches the outer ring (RADIUS/SIZE of the box), centered.
  const ZONE_INSET_PCT = (0.5 - RADIUS / SIZE) * 100;
  // The data polygon is filled with the duration-zone gradient using many thin flat-color
  // pie-slice wedges (colored via zoneColorAtDeg at each slice's midpoint) rather than a CSS
  // conic-gradient, since a CSS one (via foreignObject/clip-path) doesn't reliably scale or
  // position in mobile Safari. A true conic gradient's color depends only on angle, never
  // radius — an SVG <linearGradient> per wedge doesn't reproduce that (it interpolates along
  // its own line, so near the shared apex at CENTER it drifts toward each wedge's own
  // midpoint color instead of the true angle-based one, visibly mismatching at the seams).
  // Flat color per slice has no such distortion; ZONE_SEGMENT_COUNT is high enough that the
  // banding between slices reads as a smooth gradient. Slices are padded a hair past their
  // true angular bounds (colors are still sampled at the true, un-padded midpoint) so
  // neighboring slices overlap instead of leaving an anti-aliased seam.
  const ZONE_SEGMENT_COUNT = 180; // 2° per segment — smooth enough to read as a continuous gradient.
  const SEGMENT_OVERLAP_DEG = 0.3;
  const zoneSegments = Array.from({ length: ZONE_SEGMENT_COUNT }, (_, i) => {
    const startDeg = (360 / ZONE_SEGMENT_COUNT) * i;
    const endDeg = (360 / ZONE_SEGMENT_COUNT) * (i + 1);
    const a0 = ((startDeg - SEGMENT_OVERLAP_DEG) * Math.PI) / 180 - Math.PI / 2;
    const a1 = ((endDeg + SEGMENT_OVERLAP_DEG) * Math.PI) / 180 - Math.PI / 2;
    const x0 = CENTER + RADIUS * Math.cos(a0);
    const y0 = CENTER + RADIUS * Math.sin(a0);
    const x1 = CENTER + RADIUS * Math.cos(a1);
    const y1 = CENTER + RADIUS * Math.sin(a1);
    return {
      color: zoneColorAtDeg((startDeg + endDeg) / 2),
      wedgeD: `M${CENTER},${CENTER} L${x0},${y0} A${RADIUS},${RADIUS} 0 0,1 ${x1},${y1} Z`,
    };
  });

  // Unique per instance so multiple radars on the same page don't collide on <clipPath> id.
  const dataClipId = `radar-data-clip-${Math.random().toString(36).slice(2, 9)}`;

  $: n = entries.length;
  $: angleFor = (i: number) => (Math.PI * 2 * i) / n - Math.PI / 2;
  $: pointAt = (i: number, pct: number) => {
    const a = angleFor(i);
    const r = (RADIUS * pct) / 100;
    return { x: CENTER + r * Math.cos(a), y: CENTER + r * Math.sin(a) };
  };

  $: axisPoints = entries.map((_, i) => pointAt(i, 100));
  $: dataPoints = entries.map((e, i) => pointAt(i, Math.max(0, Math.min(100, e.percentile))));
  $: dataPath = dataPoints.length ? 'M' + dataPoints.map((p) => `${p.x},${p.y}`).join('L') + 'Z' : '';

  // Label the single most notable axis — the reader's strongest discipline relative to the population.
  $: standoutIndex = entries.reduce((best, e, i) => (e.percentile > (entries[best]?.percentile ?? -1) ? i : best), 0);

  function labelAnchor(i: number): 'start' | 'middle' | 'end' {
    const a = angleFor(i);
    const cos = Math.cos(a);
    if (cos > 0.3) return 'start';
    if (cos < -0.3) return 'end';
    return 'middle';
  }
  function labelPoint(i: number) {
    return pointAt(i, 100 + 16 * (200 / RADIUS));
  }

  // A pie-slice hit area spanning the full angular width of axis i, from the
  // center out past the axis label, so hovering anywhere near that axis — not
  // just the exact data point — surfaces its tooltip.
  const WEDGE_RADIUS = RADIUS + 40;
  $: wedgePath = (i: number) => {
    const half = Math.PI / n;
    const a0 = angleFor(i) - half;
    const a1 = angleFor(i) + half;
    const x0 = CENTER + WEDGE_RADIUS * Math.cos(a0);
    const y0 = CENTER + WEDGE_RADIUS * Math.sin(a0);
    const x1 = CENTER + WEDGE_RADIUS * Math.cos(a1);
    const y1 = CENTER + WEDGE_RADIUS * Math.sin(a1);
    return `M${CENTER},${CENTER} L${x0},${y0} A${WEDGE_RADIUS},${WEDGE_RADIUS} 0 0,1 ${x1},${y1} Z`;
  };

  let hovered: number | null = null;
  let tooltipPos = { x: 0, y: 0 };
  let containerEl: HTMLDivElement;

  function showTooltip(i: number) {
    hovered = i;
    const p = pointAt(i, Math.max(0, Math.min(100, entries[i].percentile)));
    tooltipPos = { x: (p.x / SIZE) * (containerEl?.clientWidth ?? SIZE), y: (p.y / SIZE) * (containerEl?.clientHeight ?? SIZE) };
  }
  function hideTooltip() {
    hovered = null;
  }
</script>

<div class="flex flex-col items-center gap-2">
  <div class="text-xs text-muted-foreground">
    Rings mark the population <span class="text-foreground">percentiles</span> at each discipline.
  </div>

  <div bind:this={containerEl} class="relative w-full max-w-[480px] mx-auto">
    <div
      class="absolute z-0 rounded-full"
      style="inset: {ZONE_INSET_PCT}%; background: white; opacity: {ZONE_OPACITY};"
    />
    <div
      class="absolute z-0 rounded-full"
      style="inset: {ZONE_INSET_PCT}%; background: {ZONE_GRADIENT}; mask: {BORDER_MASK}; -webkit-mask: {BORDER_MASK};"
    />
    <svg viewBox="0 0 {SIZE} {SIZE}" class="relative z-10 w-full h-auto overflow-visible" role="img" aria-label="Percentile ranking by discipline">
      <!-- Grid rings -->
      {#each RINGS as ring}
        <circle
          cx={CENTER} cy={CENTER} r={(RADIUS * ring) / 100}
          class="fill-none stroke-border"
          stroke-width="1"
        />
      {/each}
      {#each LABELED_RINGS as ring}
        <text
          x={CENTER + 4} y={CENTER - (RADIUS * ring) / 100 - 4}
          class="fill-muted-foreground"
          font-size="10"
        >{ring === 50 ? 'Median' : 'Top 10%'}</text>
      {/each}

      <!-- Axis spokes -->
      {#each axisPoints as p}
        <line x1={CENTER} y1={CENTER} x2={p.x} y2={p.y} class="stroke-border" stroke-width="1" />
      {/each}

      <!-- Data polygon: gradient fill clipped to the shape, no fade toward the center.
           Native SVG clipPath + filled pie-slice wedges (see zoneSegments above) instead of
           a CSS conic-gradient + clip-path, which doesn't reliably scale/position in mobile
           Safari. -->
      {#if dataPath}
        <clipPath id={dataClipId}>
          <path d={dataPath} />
        </clipPath>
        <g clip-path="url(#{dataClipId})" opacity={FILL_OPACITY} style="pointer-events: none;">
          {#each zoneSegments as seg}
            <path d={seg.wedgeD} fill={seg.color} />
          {/each}
        </g>
        <path d={dataPath} class="fill-none stroke-primary" stroke-width="2" stroke-linejoin="round" />
      {/if}

      <!-- Axis labels -->
      {#each entries as entry, i}
        {@const lp = labelPoint(i)}
        {@const anchor = labelAnchor(i)}
        {#if isDeprecatedAthleteName(entry.athlete)}
          <text
            x={lp.x} y={lp.y}
            text-anchor={anchor}
            dominant-baseline="middle"
            class="fill-foreground"
            font-size="11"
            font-weight={i === standoutIndex ? '600' : '400'}
          >{formatEffortDuration(entry.duration)}</text>
        {:else}
          <text
            x={lp.x} y={lp.y - 5}
            text-anchor={anchor}
            dominant-baseline="middle"
            class="fill-foreground"
            font-size="11"
            font-weight={i === standoutIndex ? '600' : '400'}
          >{entry.athlete}</text>
          <text
            x={lp.x} y={lp.y + 7}
            text-anchor={anchor}
            dominant-baseline="middle"
            class="fill-muted-foreground"
            font-size="9"
          >{formatEffortDuration(entry.duration)}</text>
        {/if}
      {/each}

      <!-- Vertices -->
      {#each dataPoints as p}
        <circle cx={p.x} cy={p.y} r="4" class="fill-primary stroke-background" stroke-width="2" />
      {/each}

      <!-- Angular hover wedges — one per axis, full pie-slice hit area anchored to the data point -->
      {#each entries as entry, i}
        <path
          d={wedgePath(i)}
          class="fill-transparent cursor-pointer"
          tabindex="0"
          role="button"
          aria-label="{disciplineLabel(entry.athlete)}: {round(entry.percentile)}{ordinalSuffix(entry.percentile)} percentile"
          on:pointerenter={() => showTooltip(i)}
          on:pointerleave={hideTooltip}
          on:focus={() => showTooltip(i)}
          on:blur={hideTooltip}
        />
      {/each}
    </svg>

    {#if hovered !== null}
      {@const e = entries[hovered]}
      {@const accent = zoneColorForIndex(hovered, n)}
      {@const pct = Math.max(0, Math.min(100, e.percentile))}
      <div
        class="pointer-events-none absolute z-10 w-56 -translate-x-1/2 -translate-y-full rounded-lg border border-border bg-popover text-popover-foreground shadow-lg"
        style="left: {tooltipPos.x}px; top: {tooltipPos.y - 10}px;"
      >
        <div class="p-3">
          <div class="flex items-center gap-1.5">
            <span class="h-2 w-2 shrink-0 rounded-full" style="background-color: {accent};" />
            <div class="text-sm font-semibold leading-tight">
              {disciplineLabel(e.athlete)}
            </div>
          </div>
          {#if !isDeprecatedAthleteName(e.athlete) && e.duration != null}
            <div class="mt-0.5 pl-3.5 text-xs text-muted-foreground leading-tight">{formatEffortDuration(e.duration)} effort</div>
          {/if}

          <div class="mt-2.5 flex items-baseline gap-1">
            <span class="text-2xl font-bold leading-none" style="color: {accent};">{round(e.percentile)}</span>
            <span class="text-xs font-medium text-muted-foreground">{ordinalSuffix(e.percentile)} percentile</span>
          </div>
          <div class="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div class="h-full rounded-full" style="width: {pct}%; background-color: {accent};" />
          </div>

          <div class="mt-3 grid grid-cols-3 gap-1 border-t border-border pt-2 text-center">
            <div>
              <div class="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">You</div>
              <div class="text-xs font-semibold text-foreground">{round(e.current, valuePrecision(e.unit))}</div>
            </div>
            <div>
              <div class="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Median</div>
              <div class="text-xs text-muted-foreground">{round(e.median, valuePrecision(e.unit))}</div>
            </div>
            <div>
              <div class="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Top 10%</div>
              <div class="text-xs text-muted-foreground">{round(e.best, valuePrecision(e.unit))}</div>
            </div>
          </div>
          <div class="mt-1 text-center text-[10px] text-muted-foreground">{e.unit}</div>

          {#if e.sourceActivity}
            <div class="mt-2 border-t border-border pt-2">
              <div class="truncate text-[11px] font-medium text-foreground" title={e.sourceActivity.name}>
                {e.sourceActivity.name}
              </div>
              <div class="mt-1 grid grid-cols-3 gap-1 text-center">
                <div>
                  <div class="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">Peak Power</div>
                  <div class="text-[11px] font-semibold text-foreground">{round(e.sourceActivity.signature.pp)} W</div>
                </div>
                <div>
                  <div class="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">HIE</div>
                  <div class="text-[11px] font-semibold text-foreground">{round(e.sourceActivity.signature.atc / 1000, 1)} kJ</div>
                </div>
                <div>
                  <div class="text-[9px] font-medium uppercase tracking-wide text-muted-foreground">Threshold</div>
                  <div class="text-[11px] font-semibold text-foreground">{round(e.sourceActivity.signature.ftp)} W</div>
                </div>
              </div>
            </div>
          {/if}
        </div>
        <div class="absolute left-1/2 top-full h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 border-b border-r border-border bg-popover" />
      </div>
    {/if}
  </div>
</div>
